/**
 * Deterministic content fingerprints shared by every cache key in the app.
 *
 * Two different snapshots of the same data must hash to the same token, so the
 * canonical form sorts object keys, keeps array order, and gives `undefined`,
 * `NaN` and `Infinity` one agreed representation instead of whatever
 * `JSON.stringify` happens to do at each call site.
 */

const CircularMarker = '[circular]';

const canonicalizeValue = (value: unknown, ancestors: Set<object>): unknown => {
  if (value === undefined || (typeof value === 'number' && !Number.isFinite(value))) {
    return null;
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  const container = value as object;
  if (ancestors.has(container)) {
    return CircularMarker;
  }
  ancestors.add(container);
  const canonical = Array.isArray(container)
    ? container.map((entry) => canonicalizeValue(entry, ancestors))
    : canonicalizeEntries(container as Record<string, unknown>, ancestors);
  ancestors.delete(container);
  return canonical;
};

const canonicalizeEntries = (
  source: Record<string, unknown>,
  ancestors: Set<object>
): Record<string, unknown> =>
  Object.keys(source)
    .sort()
    .reduce<Record<string, unknown>>((canonical, key) => {
      if (source[key] !== undefined) {
        canonical[key] = canonicalizeValue(source[key], ancestors);
      }
      return canonical;
    }, {});

/** Canonical JSON text: key-ordered, cycle-safe, and never `undefined`. */
export const stableJsonStringify = (value: unknown): string =>
  JSON.stringify(canonicalizeValue(value, new Set<object>())) ?? 'null';

/** FNV-1a over UTF-16 code units; one pass per offset basis. */
const fnv1a = (input: string, offsetBasis: number): number => {
  let hash = offsetBasis;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

const toHex = (value: number): string => value.toString(16).padStart(8, '0');

/** Namespaced 64-bit-wide token; the prefix keeps unrelated fingerprints apart. */
export const fingerprintPayload = (prefix: string, payload: string): string =>
  `${prefix}:${toHex(fnv1a(payload, 0x811c9dc5))}${toHex(fnv1a(payload, 0x9dc5811c))}`;

/** Canonicalizes and hashes in one step for the common "hash this object" case. */
export const fingerprintValue = (prefix: string, value: unknown): string =>
  fingerprintPayload(prefix, stableJsonStringify(value));

/** A missing fingerprint never matches: absence of proof is not proof of sameness. */
export const areFingerprintsEqual = (
  left: string | null | undefined,
  right: string | null | undefined
): boolean => Boolean(left) && Boolean(right) && left === right;
